import { HttpBackend, HttpClient } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { MadCloudResult } from '@common/http/models/mad-cloud-result.model';
import { PageFilter } from '@common/paged-data/types/page-filter.type';
import { Page } from '@common/paged-data/types/page.type';
import { ApiService } from '@portal-core/auth/services/api.service';
import { CreateStatusIncident } from '@portal-core/status-incidents/models/status-incident-create.model';
import { StatusIncidentUpdate } from '@portal-core/status-incidents/models/status-incident-update.model';
import { StatusIncident } from '@portal-core/status-incidents/models/status-incident.model';
import { closingStatusFor } from '@portal-core/status-incidents/util/status-incident.util';
import { SharePendingRequest } from '@portal-core/util/observables.decorators';
import { forkJoin, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Service()
export class StatusIncidentsApiService {
  private readonly http = inject(HttpClient);
  private readonly apiService = inject(ApiService);

  /** Built on the backend directly, so it carries none of the interceptors the authenticated client has. */
  private readonly anonymousHttp: HttpClient;

  constructor() {
    this.anonymousHttp = new HttpClient(inject(HttpBackend));
  }

  /**
   * Reads the open incident from the status service, which is a separate anonymous service on its own
   * origin. AuthInterceptorService sets withCredentials on every request it sees, and a credentialed
   * cross-origin request obliges that service to name this origin back rather than answering any origin.
   */
  @SharePendingRequest()
  getActiveStatusIncident$(): Observable<StatusIncident> {
    return this.getCurrent$(this.apiService.statusPageApiBaseUri, this.anonymousHttp);
  }

  @SharePendingRequest()
  getActiveStatusIncidentAdmin$(): Observable<StatusIncident> {
    return this.getCurrent$(this.apiService.centralApiBaseUri, this.http);
  }

  @SharePendingRequest()
  getStatusIncidentsHistoryPage$(filter: PageFilter): Observable<Page<StatusIncident>> {
    return this.http.post<Page<StatusIncident>>(`${this.apiService.centralApiBaseUri}/api/StatusIncidents/history`, filter);
  }

  /**
   * Creates the incident together with its initial updates.
   *
   * The updates are sent inline rather than through their own endpoint so that the incident and the update
   * carrying its status are written in one transaction. The server rejects an incident that arrives without
   * one, and the created incident comes back with its updates already attached.
   */
  createStatusIncident$(incident: CreateStatusIncident): Observable<StatusIncident> {
    return this.http.post<StatusIncident>(`${this.apiService.centralApiBaseUri}/api/StatusIncidents`, incident);
  }

  /**
   * Closes every open incident.
   *
   * There is no bulk endpoint: an incident closes by moving its own status, and which status that is depends
   * on whether it was scheduled maintenance or unplanned.
   */
  deactivateStatusIncidents$(): Observable<MadCloudResult> {
    return this.http.get<StatusIncident[]>(`${this.apiService.centralApiBaseUri}/api/StatusIncidents/current`).pipe(
      switchMap(incidents => {
        const open = incidents ?? [];
        if (open.length === 0) {
          return of({} as MadCloudResult);
        }

        return forkJoin(open.map(incident => this.updateStatusIncident$(incident.Id, {
          ...incident,
          Status: closingStatusFor(incident.Type),
          ResolvedAt: new Date().toISOString()
        }))).pipe(map(() => ({} as MadCloudResult)));
      })
    );
  }

  publishStatusIncident$(incidentId: number): Observable<StatusIncident> {
    return this.http.post<StatusIncident>(`${this.apiService.centralApiBaseUri}/api/StatusIncidents/${incidentId}/publish`, null);
  }

  unpublishStatusIncident$(incidentId: number): Observable<StatusIncident> {
    return this.http.post<StatusIncident>(`${this.apiService.centralApiBaseUri}/api/StatusIncidents/${incidentId}/unpublish`, null);
  }

  updateStatusIncident$(incidentId: number, incident: StatusIncident): Observable<StatusIncident> {
    return this.http.put<StatusIncident>(`${this.apiService.centralApiBaseUri}/api/StatusIncidents/${incidentId}`, incident);
  }

  deleteStatusIncidentById$(incident: StatusIncident) {
    return this.http.delete<MadCloudResult>(`${this.apiService.centralApiBaseUri}/api/StatusIncidents/${incident.Id}`);
  }

  addStatusIncidentUpdate$(incidentId: number, update: Partial<StatusIncidentUpdate>): Observable<StatusIncidentUpdate> {
    return this.http.post<StatusIncidentUpdate>(`${this.apiService.centralApiBaseUri}/api/StatusIncidents/${incidentId}/updates`, update);
  }

  updateStatusIncidentUpdate$(incidentId: number, update: StatusIncidentUpdate): Observable<StatusIncidentUpdate> {
    return this.http.put<StatusIncidentUpdate>(`${this.apiService.centralApiBaseUri}/api/StatusIncidents/${incidentId}/updates/${update.Id}`, update);
  }

  deleteStatusIncidentUpdate$(incidentId: number, updateId: number): Observable<MadCloudResult> {
    return this.http.delete<MadCloudResult>(`${this.apiService.centralApiBaseUri}/api/StatusIncidents/${incidentId}/updates/${updateId}`);
  }

  private getCurrent$(baseUri: string, http: HttpClient): Observable<StatusIncident> {
    // Returns every open incident, newest first; callers that show one at a time take the newest.
    return http.get<StatusIncident[]>(`${baseUri}/api/StatusIncidents/current`).pipe(
      map(incidents => incidents?.[0])
    );
  }
}